import { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import Modal from "./index";
import Button from "../Button";

const SlideOver = ({
  open,
  onClose,
  actionButton,
  title,
  children,
  footer,
  dirty = false,
}) => {
  const { t } = useTranslation();

  // Escape and a backdrop click are the two ways out nobody means to take, and
  // they used to throw away a half-filled form without asking. They are the only
  // paths guarded here: the header X and a form's own Cancel button are
  // deliberate, so they still close straight away.
  //
  // `dirty` is what a form tells us; `touched` is the fallback for the modals
  // that do not. React does not fire input/change for values a form fills in
  // itself, so a read-only detail modal never arms the guard.
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // While the panel is still sliding in, most of it is off screen, and the
  // panel it replaced is still sliding out under the same spot. A click aimed
  // at it lands on the old panel or on the new backdrop and reads as "outside":
  // clicking Pilih invoice right after Pakai di Invoice closed the deposit
  // altogether. Pointer closes are ignored until the panel is in place; Escape,
  // the X and Cancel are unaffected. The click is watched on the whole document
  // because the old panel is another dialog, outside this one's tree.
  const entering = useRef(false);
  // Not 0: performance.now() starts near 0 when the page loads, and a panel
  // opened in its first second would then ignore Escape as well.
  const lastPointerDown = useRef(-Infinity);

  useEffect(() => {
    if (open) {
      setTouched(false);
      setConfirming(false);
      entering.current = true;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const notePointerDown = () => {
      lastPointerDown.current = performance.now();
    };
    document.addEventListener("pointerdown", notePointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", notePointerDown, true);
  }, [open]);

  const markTouched = () => setTouched(true);

  const requestClose = () => {
    // Headless UI still counts the dialog as open while it slides out, so a
    // panel that has already been closed can report an outside click or its
    // own disappearance for another 500ms. The panels that swap one for
    // another (a deposit's detail for Pakai di Invoice, an invoice for one of
    // its deposits) share one onClose that shuts the whole modal, so that late
    // call closed the panel that had just taken its place. A closed panel has
    // nothing left to close.
    if (!open) return;
    if (
      entering.current &&
      performance.now() - lastPointerDown.current < 1000
    ) {
      return;
    }
    if (dirty || touched) {
      setConfirming(true);
      return;
    }
    onClose();
  };

  const discard = () => {
    setConfirming(false);
    onClose();
  };

  return (
    <>
      <Transition show={open}>
        <Dialog as="div" className="relative z-50" onClose={requestClose}>
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <TransitionChild
                  enter="transform transition ease-in-out duration-500"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                  afterEnter={() => {
                    entering.current = false;
                  }}
                >
                  <DialogPanel className="pointer-events-auto w-screen max-w-md">
                    <div
                      className="flex h-full flex-col bg-white shadow-xl"
                      onInput={markTouched}
                      onChange={markTouched}
                    >
                      <div className="px-4 py-6 sm:px-6">
                        <div className="flex items-start justify-between">
                          <DialogTitle className="text-lg font-medium">
                            {t(title)}
                          </DialogTitle>
                          <div className="flex items-center space-x-2">
                            {actionButton}
                            <button
                              type="button"
                              className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                              onClick={onClose}
                            >
                              <span className="sr-only">{t("common.close")}</span>
                              <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex-1 overflow-y-auto px-4 sm:px-6">
                        {children}
                      </div>

                      {footer && (
                        <div className="flex-shrink-0 border-t px-4 py-5 sm:px-6 bg-gray-50">
                          {footer}
                        </div>
                      )}
                    </div>
                  </DialogPanel>
                </TransitionChild>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Nested on purpose: HeadlessUI keeps a dialog stack, so Escape here
          dismisses the confirmation only and leaves the form standing. */}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="common.unsavedChanges.title"
        footer={
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirming(false)}
            >
              {t("common.unsavedChanges.keepEditing")}
            </Button>
            <Button
              type="button"
              variant="primary"
              color="red"
              onClick={discard}
            >
              {t("common.unsavedChanges.discard")}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-500">
          {t("common.unsavedChanges.body")}
        </p>
      </Modal>
    </>
  );
};

export default SlideOver;
